import axios from "axios";
import { BaseResponseSingle } from "../../typings";
import {
  IStoreConfigResponse,
  IStoreConfigSave,
} from "../../typings/interfaces/responses/store-config-response";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { ADD_STORE_CONFIG, GET_STORE_CONFIG } from "../../configs";
import { setStoreTimeZone } from "../../util/store-clock";
import { loadStoreSetup } from "../../util/store-setup-loader";
import {
  setReadyDateConfig,
  setServiceReadyTimes,
} from "../../configs/readyDateTable";

export const getStoreConfig = createAsyncThunk(
  "store-config/getById",
  async (
    { storeId, merchantId }: { storeId: number; merchantId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<IStoreConfigResponse>
      >(GET_STORE_CONFIG(storeId, merchantId));
      // Receipt / report store header; doesn't hold up the config
      void loadStoreSetup(storeId, merchantId);
      setStoreTimeZone(response.data?.body?.autoCloseTimezone);
      setReadyDateConfig(
        response.data?.body?.readyDateMode,
        response.data?.body?.readyDateRules
      );
      setServiceReadyTimes(
        response.data?.body?.regularReadyTime,
        response.data?.body?.specialReadyTime
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Re-reads the store config without clearing the loaded one while it's in
// flight (getStoreConfig's pending/rejected reset it), so open screens keep
// their dates until the new admin rules arrive
export const refreshStoreConfig = createAsyncThunk(
  "store-config/refresh",
  async (
    { storeId, merchantId }: { storeId: number; merchantId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get<
        BaseResponseSingle<IStoreConfigResponse>
      >(GET_STORE_CONFIG(storeId, merchantId));
      // Receipt / report store header; doesn't hold up the config
      void loadStoreSetup(storeId, merchantId);
      setStoreTimeZone(response.data?.body?.autoCloseTimezone);
      setReadyDateConfig(
        response.data?.body?.readyDateMode,
        response.data?.body?.readyDateRules
      );
      setServiceReadyTimes(
        response.data?.body?.regularReadyTime,
        response.data?.body?.specialReadyTime
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addStoreConfig = createAsyncThunk(
  "store-config/add",
  async (
    {
      storeId,
      merchantId,
      cutOffTime,
      pickupTime,
      workingDays,
      daysToDeliver,
      specialDaysToDeliver,
      autoCloseEnabled,
      autoCloseTime,
      autoCloseTimezone,
      autoCloseGraceMinutes,
      readyDateMode,
      readyDateRules,
      regularServiceEnabled,
      specialServiceEnabled,
      regularReadyTime,
      specialReadyTime,
      clearRegularReadyTime,
      clearSpecialReadyTime,
    }: IStoreConfigSave,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<IStoreConfigResponse>
      >(ADD_STORE_CONFIG, {
        storeId,
        merchantId,
        cutOffTime,
        pickupTime,
        workingDays,
        daysToDeliver,
        specialDaysToDeliver,
        autoCloseEnabled,
        autoCloseTime,
        autoCloseTimezone,
        autoCloseGraceMinutes,
        readyDateMode,
        readyDateRules,
        regularServiceEnabled,
        specialServiceEnabled,
        regularReadyTime,
        specialReadyTime,
        clearRegularReadyTime,
        clearSpecialReadyTime,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
